import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));

  // Add these permission functions
  const hasPermission = (permission) => {
    if (!admin?.permissions) return false;
    return admin.permissions[permission] === true;
  };

  const isSuperAdmin = () => {
    return admin?.role === 'super_admin';
  };

  const isMinister = () => {
    return admin?.role === 'minister_admin';
  };

  const isSupport = () => {
    return admin?.role === 'support_admin';
  };

  const verifyToken = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await adminAPI.verifyToken();
      // Use the exact response structure from your backend
      setAdmin(response.data.data); // response.data.data contains the admin object
      
      // Also store in localStorage for Admin Management component
      localStorage.setItem('currentAdmin', JSON.stringify(response.data.data));
    } catch (error) {
      console.error('Token verification failed:', error);
      setAdmin(null);
      setToken(null);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('currentAdmin');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token, verifyToken]);

  const login = async (email, password) => {
    try {
      const response = await adminAPI.login({ email, password });
      
      // Match your backend response structure exactly
      const { token: newToken, admin: adminData } = response.data.data;
      
      localStorage.setItem('adminToken', newToken);
      setToken(newToken);
      setAdmin(adminData);
      
      // ✅ FIXED: Store admin data for Admin Management component
      localStorage.setItem('currentAdmin', JSON.stringify(adminData));
      sessionStorage.setItem('currentAdmin', JSON.stringify(adminData));
      
      console.log('✅ Login successful - Admin data stored:', {
        email: adminData.email,
        role: adminData.role,
        id: adminData.id
      });
      
      return { 
        success: true, 
        admin: adminData // ✅ FIXED: Return admin data to Login component
      };
    } catch (error) {
      console.error('❌ Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed. Please check your credentials.' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('currentAdmin');
    sessionStorage.removeItem('currentAdmin');
    setToken(null);
    setAdmin(null);
  };

  const value = {
    admin,
    login,
    logout,
    loading,
    isAuthenticated: !!admin && !!token,
    // FIX: Pass the functions, don't call them immediately
    hasPermission,
    isSuperAdmin: isSuperAdmin, // Pass function reference
    isMinister: isMinister,     // Pass function reference  
    isSupport: isSupport        // Pass function reference
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};